import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';

import {
  ReadingGroup,
  ReadingGroupJoinStatus,
  ReadingGroupMember,
  ReadingGroupType,
} from 'shared/models';
import {
  APIError,
  OthersBookshelfOpenerService,
  ReadingGroupMembersAPIService,
} from 'shared/services';
import { AlertService, MenuPositionBottomLeft, ToastService } from 'shared/ui';
import { ReadingGroupPasscodeDialogComponent } from '../../dialogs/reading-group-passcode-dialog/reading-group-passcode-dialog.component';
import { ReadingGroupExitDialogComponent } from '../../dialogs/reading-group-exit-dialog/reading-group-exit-dialog.component';

enum Tab {
  description,
  joinedMembers,
  kickedMembers,
  invitation,
}

function createInvitationTextHTML(readingGroup: ReadingGroup): string {
  return `북이오에서 ‘${readingGroup.name}’ 그룹책을 함께읽어요. 함께읽기는 책 구매 후 바로 가능합니다!
${location.origin}/@${readingGroup.content.id}?invitedGroupId=${readingGroup.id}`;
}

@Component({
  selector: 'lib-reading-group-details',
  templateUrl: './reading-group-details.component.html',
  styleUrl: './reading-group-details.component.scss',
})
export class ReadingGroupDetailsComponent implements OnInit {
  @ViewChild('copyText') private _copyTextElem?: ElementRef<HTMLDivElement>;

  @Input() group!: ReadingGroup;
  @Input() isUserLoggedIn = true;
  @Input() bookInfo?: { title: string; cover: string; author: string };

  @Output() joinGroupButtonClicked = new EventEmitter<void>();
  @Output() editGroupButtonClick = new EventEmitter<void>();
  @Output() enterGroupButtonClick = new EventEmitter<number>();
  @Output() inviteGroupMemberButtonClick = new EventEmitter<void>();
  @Output() groupExited = new EventEmitter<void>();
  @Output() groupJoined = new EventEmitter<void>();

  public _JoinStatus = ReadingGroupJoinStatus;
  public _Tab = Tab;
  public _joinedMembers?: ReadingGroupMember[];
  public _kickedMembers?: ReadingGroupMember[];
  public _openedAccordion: Tab | undefined;

  public _invitationText!: string;

  public _isJoining = false;

  public readonly _moreMenuPosition = MenuPositionBottomLeft;

  constructor(
    private _clipboard: Clipboard,
    private _readingGroupMembersAPIService: ReadingGroupMembersAPIService,
    private _alertService: AlertService,
    private _toastService: ToastService,
    private _othersBookshelfOpenerService: OthersBookshelfOpenerService
  ) {}

  get _isGroupMember(): boolean {
    return (
      this.group.status === ReadingGroupJoinStatus.joined ||
      this.group.status === ReadingGroupJoinStatus.owned
    );
  }

  get _canInviteMember(): boolean {
    return (
      this.group.status === ReadingGroupJoinStatus.joined ||
      this.group.status === ReadingGroupJoinStatus.owned
    );
  }

  get _canKickMember(): boolean {
    return this.group.status === ReadingGroupJoinStatus.owned;
  }

  ngOnInit(): void {
    // 그룹책 소개는 비공개 그룹이 아닌 경우는 기본으로 열리도록 설정
    if (this.group.type !== ReadingGroupType.private) {
      this._openedAccordion = Tab.description;
    }

    this._invitationText = createInvitationTextHTML(this.group);
  }

  _onCopyInvitationTextButtonClick(): void {
    const text = this._invitationText;
    text && this._clipboard.copy(text);
    this._toastService.open('복사가 완료되었습니다.');
  }

  _onOpenAccordion(tab: Tab): void {
    this._openedAccordion = tab;

    if (tab === Tab.joinedMembers) {
      this._loadJoinedMembers();
    } else if (tab === Tab.kickedMembers) {
      this._loadKickedMembers();
    }
  }

  _onCloseAccordion(tab: Tab): void {
    if (this._openedAccordion === tab) {
      this._openedAccordion = undefined;
    }
  }

  private _openPasscodeDialog(): void {
    const dialogRef = this._alertService.open(
      ReadingGroupPasscodeDialogComponent,
      {
        data: {
          groupId: this.group.id,
        },
      }
    );

    dialogRef.afterClosed().subscribe((joined) => {
      if (joined) {
        this._toastService.open('그룹책 읽기가 시작되었습니다.');
        this.enterGroupButtonClick.emit(this.group.id);
      }
    });
  }

  _onEnterGroupButtonClick(): void {
    /*
      1. 비공개 그룹
      2. 초대된 상태가 아님
      3. 가입된 상태가 아님
      4. 그룹장 상태가 아님

      1 ~ 4번이 모두 만족하는 경우만 비밀번호 입력 UI가 노출되어야함.
    */
    if (
      this.group.type === ReadingGroupType.private &&
      this.group.status !== ReadingGroupJoinStatus.invited &&
      this.group.status !== ReadingGroupJoinStatus.joined &&
      this.group.status !== ReadingGroupJoinStatus.owned
    ) {
      this._openPasscodeDialog();
      return;
    }

    this.enterGroupButtonClick.emit(this.group.id);
  }

  _onEditGroupButtonClick(): void {
    this.editGroupButtonClick.emit();
  }

  _onExitGroupButtonClick(): void {
    this._alertService
      .open(ReadingGroupExitDialogComponent, {
        data: {
          groupId: this.group.id,
          status: this.group.status,
        },
      })
      .afterClosed()
      .subscribe((exited) => {
        exited && this.groupExited.emit();
      });
  }

  _onKickUserButtonClick(index: number): void {
    const member = this._joinedMembers?.[index];

    if (!member) {
      return;
    }

    this._alertService
      .openConfirm(
        `${member.nickname}님을 그룹책에서 내보내기 하시겠습니까?`,
        "한번 내보내기된 멤버는 만든이가 직접 '내보내기 해제' 하지 않는 한 그룹책 읽기가 불가능합니다.",
        '내보내기'
      )
      .afterClosed()
      .subscribe((ok) => {
        if (!ok) {
          return;
        }

        this._readingGroupMembersAPIService
          .kick(this.group.id, member.id)
          .subscribe({
            next: () => {
              this._loadJoinedMembers();
              this._loadKickedMembers();
            },
            error: (error: APIError) => {
              this._toastService.openWarning(error.message);
            },
          });
      });
  }

  _onUnkickUserButtonClick(index: number): void {
    const member = this._kickedMembers?.[index];

    if (!member) {
      return;
    }

    this._alertService
      .openConfirm(
        undefined,
        `${member.nickname}님을 그룹 내보내기에서 해제하시겠습니까?`
      )
      .afterClosed()
      .subscribe((ok) => {
        if (!ok) {
          return;
        }

        this._readingGroupMembersAPIService
          .unkick(this.group.id, member.id)
          .subscribe({
            next: () => {
              this._loadKickedMembers();
              this._loadJoinedMembers();
            },
            error: (error: APIError) => {
              this._toastService.openWarning(error.message);
            },
          });
      });
  }

  _onInviteGroupMemberButtonClick(): void {
    this.inviteGroupMemberButtonClick.emit();
  }

  private _loadJoinedMembers(): void {
    this._readingGroupMembersAPIService
      .getJoined(this.group.id)
      .subscribe((members) => {
        this._joinedMembers = members;
      });
  }

  private _loadKickedMembers(): void {
    this._readingGroupMembersAPIService
      .getKicked(this.group.id)
      .subscribe((members) => {
        this._kickedMembers = members;
      });
  }

  _onReadingGroupMemberClick(member: ReadingGroupMember): void {
    if (member.is_me) {
      return;
    }

    this._othersBookshelfOpenerService.open(member.id);
  }
}
